import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import axios from 'axios';

import { API_BASE_URL } from '../config/api';

const DEFAULT_PAGE_PLACEHOLDER = '__defaultPage__';

const normalizeMenuItems = (items, defaultPagePath = '/') => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .map((item) => {
      if (!item || !item.label) {
        return null;
      }

      const rawPath = typeof item.path === 'string' ? item.path.trim() : '';
      const resolvedPath =
        rawPath === DEFAULT_PAGE_PLACEHOLDER
          ? defaultPagePath
          : rawPath || '/';

      return {
        label: item.label,
        path: resolvedPath
      };
    })
    .filter(Boolean);
};

function Header({ isAdmin = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { getCartItemCount } = useCart();
  const cartItemCount = getCartItemCount();
  const [storeName, setStoreName] = useState('Digital Designs Store');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [defaultPage, setDefaultPage] = useState('/');
  const [menuItems, setMenuItems] = useState([]);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/config`);
        if (response.data.success) {
          if (response.data.data.store && response.data.data.store.name) {
            setStoreName(response.data.data.store.name);
          }
          // Get defaultPage from config, default to "/" if not present or missing
          const configDefaultPage = response.data.data.defaultPage;
          const resolvedDefaultPage =
            configDefaultPage && configDefaultPage.trim()
              ? configDefaultPage.trim()
              : '/';
          setDefaultPage(resolvedDefaultPage);

          const normalizedMenuItems = normalizeMenuItems(
            response.data.data.mainMenu,
            resolvedDefaultPage
          );
          setMenuItems(normalizedMenuItems);
        }
      } catch (err) {
        console.error('Error loading store config:', err);
      }
    };
    fetchConfig();
  }, []);

  // Check if user is logged in as admin
  useEffect(() => {
    const checkLogin = async () => {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      if (token) {
        try {
          const response = await axios.get(`${API_BASE_URL}/auth/verify`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setIsLoggedIn(response.data.success === true);
        } catch (err) {
          // Token invalid or expired - silently handle (expected for non-logged-in users)
          setIsLoggedIn(false);
          // Clear invalid token
          if (err.response?.status === 401 || err.response?.status === 403) {
            localStorage.removeItem('authToken');
            sessionStorage.removeItem('authToken');
          }
        }
      } else {
        setIsLoggedIn(false);
      }
    };
    
    checkLogin();
    
    // Listen for storage changes (when login happens in another tab)
    const handleStorageChange = (e) => {
      if (e.key === 'authToken' || e.key === null) {
        checkLogin();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Don't show header on login page
  // Allow /admin and /admin/gallery to show header
  if (location.pathname === '/login') {
    return null;
  }

  const fallbackMenuItems = [
    { label: 'Gallery', path: defaultPage },
    { label: 'Search', path: '/search' }
  ];
  const menuToRender = menuItems.length > 0 ? menuItems : fallbackMenuItems;

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to={defaultPage} className="flex items-center">
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">
                  {storeName} <span className="text-xs font-normal text-gray-500">(v1.11.0)</span>
            </h1>
          </Link>
          
          <nav className="flex items-center gap-4">
            {menuToRender.map((item) => (
              <Link
                key={`${item.label}-${item.path}`}
                to={item.path}
                className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
              >
                {item.label}
              </Link>
            ))}
            
            {/* Admin Menu Item - Show if logged in as admin */}
            {(isAdmin || isLoggedIn) && (
              <Link
                to="/admin"
                className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
              >
                Admin
              </Link>
            )}
            
            {/* Shopping Cart Icon */}
            <button
              onClick={() => navigate('/checkout')}
              className="relative p-2 text-gray-700 hover:text-blue-600 transition-colors"
              title="View Cart"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              {cartItemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                  {cartItemCount > 99 ? '99+' : cartItemCount}
                </span>
              )}
            </button>
          </nav>
        </div>
      </div>
    </header>
  );
}

export default Header;

